import { computed, toValue, useAttrs, watch } from "vue";

import { DEFAULT_COUNT, MAX_COUNT } from "#ce/components/SpOtpInput/props.js";
import { useTextModel } from "#ce/composables/useTextModel";
import { stripNonDigits } from "#ce/utils/input-masks";
import { toNumber } from "#ce/utils/props";

/**
 * The digits of a one-time passcode, one string per box, on top of `useTextModel`: the `value`
 * attribute is the default, a script write or a user edit stops the attribute from moving the
 * digits, and a form reset restores the default. No DOM access.
 *
 * A value is accepted only as a string of exactly `count` digits; anything else reads as empty.
 * Must run inside a component setup, because the model reads the host attributes.
 *
 * @param {object} options
 * @param {import("vue").MaybeRefOrGetter<number|string|undefined>} options.count - Requested box count.
 *   A value that is not a whole number from 1 to `MAX_COUNT` falls back to `DEFAULT_COUNT`.
 * @returns {{
 *   count: import("vue").ComputedRef<number>,
 *   digits: Readonly<import("vue").Ref<string[]>>,
 *   value: import("vue").ComputedRef<string>,
 *   defaultValue: import("vue").ComputedRef<string>,
 *   parseValue: (candidate: *) => string,
 *   setDigit: (index: number, digit: string) => void,
 *   writeUserValue: (candidate: *) => void,
 *   writeValue: (candidate: *) => void,
 *   adoptDefault: (candidate: *) => void,
 *   restoreDefault: () => void,
 * }}
 */
export function useOtpDigits({ count: countSource }) {
  const attrs = useAttrs();

  const count = computed(() => {
    const parsed = toNumber(toValue(countSource), NaN);

    return Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_COUNT ? parsed : DEFAULT_COUNT;
  });

  function parseValue(candidate) {
    const text = typeof candidate === "string" ? candidate : "";

    return text.length === count.value && stripNonDigits(text) === text ? text : "";
  }

  function splitDigits(text) {
    return text === "" ? Array(count.value).fill("") : text.split("");
  }

  function toDigits(candidate) {
    return Array.isArray(candidate) ? candidate : splitDigits(parseValue(candidate));
  }

  const model = useTextModel({ normalizeText: toDigits, normalizeDefault: toDigits });
  const digits = model.text;
  const value = computed(() => digits.value.join(""));
  const defaultValue = computed(() => model.defaultValue.value.join(""));

  function setDigit(index, digit) {
    const next = [...digits.value];
    next[index] = digit;
    model.setUserText(next);
  }

  // A count change moves the digits to the new default without releasing the dirty flag.
  function resetForCount() {
    model.setDefaultValue(attrs.value);

    if (model.isDirty.value) {
      model.setText(model.defaultValue.value);
    }
  }

  watch(count, resetForCount);

  return {
    count,
    digits,
    value,
    defaultValue,
    parseValue,
    setDigit,
    writeUserValue: model.setUserText,
    writeValue: model.setText,
    adoptDefault: model.setDefaultValue,
    restoreDefault: model.reset,
  };
}
